import { compareDesc, parseISO, formatISO } from 'date-fns';
import apiClient from '../shared/apiClient';
import { 
  getEmployee,
  employeesByClient,
  listLocationEmployees,
  locationEmployeesByLocation,
  orgUnitEmployeesByOrgUnit
} from 'graphql/queries';
import {
  createLocationEmployee,
  updateLocationEmployee
} from 'graphql/mutations';

/**
 * Service for managing manager assignments
 * Copied from locationAdminService.js with minimal changes
 */
class ManagerService {
  // Get manager assignments for a location
  async getManagerAssignmentsForLocation(clientId, locationId, orgUnitIds = []) {
    try {
      const managerAssignments = {
        storeManagers: [],
        hierarchicalManagers: {}
      };

      // Fetch location employees (managers) for this location
      const locationEmployeesResult = await apiClient.query(locationEmployeesByLocation, {
        locationId: locationId,
        filter: {
          isActive: { eq: true },
          clientId: { eq: clientId }
        }
      });
      
      const locationEmployees = locationEmployeesResult?.data?.locationEmployeesByLocation?.items || [];
      const results = [];
      
      // Process location employees to extract manager information
      if (locationEmployees.length > 0) {
        results.push({ 
          unitId: locationId, 
          assignments: locationEmployees.map(emp => ({
            ...emp,
            userId: emp.employeeId,
            managementLevel: 'LOCATION_LEVEL',
            managerNameSnapshot: emp.employeeNameSnapshot,
            assignedBy: emp.assignedBy,
            assignmentReason: emp.assignmentReason,
            effectiveDate: emp.effectiveDate,
            metadata: emp.metadata
          }))
        });
      }
      
      // Fetch OrgUnitEmployee assignments for all relevant org units
      const assignmentPromises = orgUnitIds.map(async (unitId) => {
        try {
          const assignmentsResult = await apiClient.query(orgUnitEmployeesByOrgUnit, {
            orgUnitId: unitId,
            filter: {
              isActive: { eq: true },
              clientId: { eq: clientId }
            }
          });
          
          const assignments = assignmentsResult?.data?.orgUnitEmployeesByOrgUnit?.items || [];
          
          if (assignments.length > 0) {
            // Map OrgUnitEmployee fields to expected format
            return { 
              unitId, 
              assignments: assignments.map(emp => ({
                ...emp,
                userId: emp.employeeId,
                managementLevel: emp.orgLevel || 'ORGLEVEL_1', // Use the orgLevel from OrgUnitEmployee
                managerNameSnapshot: emp.employeeNameSnapshot,
                orgUnitNameSnapshot: emp.orgUnitNameSnapshot,
                assignedBy: emp.assignedBy,
                assignmentReason: emp.assignmentReason,
                effectiveDate: emp.effectiveDate,
                metadata: emp.metadata
              }))
            };
          }
          
          return { unitId, assignments: [] };
        } catch (error) {
          console.error(`Error fetching OrgUnitEmployee for unit ${unitId}:`, error);
          return { unitId, assignments: [] };
        }
      });

      const orgUnitResults = await Promise.all(assignmentPromises);
      results.push(...orgUnitResults);
      
      // Collect all unique employee IDs that we need to fetch
      const employeeIds = new Set();
      results.forEach(({ assignments }) => {
        assignments.forEach(assignment => {
          if (assignment.employeeId || assignment.userId) {
            employeeIds.add(assignment.employeeId || assignment.userId);
          }
          if (assignment.assignedBy) {
            employeeIds.add(assignment.assignedBy);
          }
        });
      });

      // Fetch employee details for all employees
      const employeeMap = {};
      const employeePromises = Array.from(employeeIds).map(async (employeeId) => {
        try {
          const employeeResult = await apiClient.query(getEmployee, { id: employeeId });
          const employee = employeeResult?.data?.getEmployee;
          if (employee) {
            employeeMap[employeeId] = employee;

          }
        } catch (error) {
        }
      });

      await Promise.all(employeePromises);

      // Process assignments and categorize them
      results.forEach(({ unitId, assignments }) => {
        assignments.forEach(assignment => {
          const employeeId = assignment.employeeId || assignment.userId;
          const employee = employeeMap[employeeId];
          const assignedByEmployee = employeeMap[assignment.assignedBy];
          
          const enrichedAssignment = {
            ...assignment,
            employeeId: employeeId, // Ensure employeeId is always set
            userName: employee ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() : assignment.employeeNameSnapshot || assignment.managerNameSnapshot || 'Unknown',
            firstName: employee?.firstName || '',
            lastName: employee?.lastName || '',
            email: employee?.email || '',
            assignedByName: assignedByEmployee ? `${assignedByEmployee.firstName || ''} ${assignedByEmployee.lastName || ''}`.trim() : 'System',
            metadata: assignment.metadata || null
          };

          // Check if this is a location-level manager
          // Location managers are assigned directly to the location's ID with LOCATION_LEVEL management level
          if (assignment.managementLevel === 'LOCATION_LEVEL' && unitId === locationId) {
            managerAssignments.storeManagers.push(enrichedAssignment);
          } else if (orgUnitIds.includes(unitId)) {
            // It's a hierarchical manager (assigned to one of the org units)
            const level = assignment.managementLevel;
            if (!managerAssignments.hierarchicalManagers[level]) {
              managerAssignments.hierarchicalManagers[level] = [];
            }

            managerAssignments.hierarchicalManagers[level].push(enrichedAssignment);
          }
        });
      });

      // Sort store managers by effective date (most recent first)
      managerAssignments.storeManagers.sort((a, b) => 
        compareDesc(parseISO(a.effectiveDate), parseISO(b.effectiveDate))
      );

      // Sort hierarchical managers by level and then by effective date
      Object.keys(managerAssignments.hierarchicalManagers).forEach(level => {
        managerAssignments.hierarchicalManagers[level].sort((a, b) => 
          compareDesc(parseISO(a.effectiveDate), parseISO(b.effectiveDate))
        );
      });

      return managerAssignments;
    } catch (error) {
      console.error('❌ Error fetching manager assignments:', error);
      return {
        storeManagers: [],
        hierarchicalManagers: {}
      };
    }
  }

  // Get available managers for assignment (using Employee table)
  async getAvailableManagers(clientId) {
    try {
      const result = await apiClient.query(employeesByClient, { 
        clientId: clientId,
        filter: { 
          isActive: { eq: true } // Only show active employees
        },
        limit: 1000 
      });

      const employees = result?.data?.employeesByClient?.items || [];
      
      // Return employees formatted for the autocomplete
      return employees.map(employee => ({
        id: employee.id, // Employee.id
        employeeId: employee.id, // For clarity
        userId: employee.userId, // Reference to User.id if needed
        userName: `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.email,
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        email: employee.email || '',
        department: employee.department || '',
        position: employee.position || ''
      })).sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } catch (error) {
      console.error('Error fetching available managers:', error);
      throw error;
    }
  }

  // Update location managers (add new managers without removing existing ones)
  async updateLocationManagers(locationId, managers, clientId, locationName = 'Location') {
    try {
      // Fetch existing location employees
      const existingAssignments = await apiClient.query(locationEmployeesByLocation, {
        locationId: locationId,
        filter: {
          clientId: { eq: clientId },
          isActive: { eq: true }
        }
      });

      const existingManagers = existingAssignments?.data?.locationEmployeesByLocation?.items || [];
      const existingManagerNames = existingManagers.map(m => 
        m.employeeNameSnapshot || 'Unknown Manager'
      );
      
      // NOTE: We are NOT deactivating existing managers - each assignment is independent
      // Only deactivate if we're explicitly removing managers (which would be a different operation)
      
      // Check which managers are actually new (not already assigned)
      const newManagers = managers.filter(newManager => {
        const isAlreadyAssigned = existingManagers.some(existing => 
          existing.employeeId === (newManager.employeeId || newManager.id)
        );
        return !isAlreadyAssigned;
      });
      
      if (newManagers.length === 0) {
        return [];
      }

      // Create new manager assignments (only for new managers)
      const createPromises = newManagers.map(manager => {
        const managerFullName = `${manager.firstName} ${manager.lastName}`.trim() || manager.userName;
        const now = new Date().toISOString();
        
        // Ensure we have a valid assignedBy value (required field)
        if (!manager.assignedBy) {
          console.error('❌ Missing assignedBy field for manager:', manager);
          throw new Error('assignedBy field is required for creating LocationEmployee');
        }
        
        // Parse and format the effectiveDate to ensure it's in ISO format
        let effectiveDateISO = now;
        if (manager.effectiveDate) {
          try {
            // Parse the date and convert to ISO string (removes timezone offset)
            const parsedDate = new Date(manager.effectiveDate);
            effectiveDateISO = parsedDate.toISOString();
          } catch (e) {
            console.warn('⚠️ Could not parse effectiveDate, using current time:', e);
            effectiveDateISO = now;
          }
        }
        
        const input = {
          clientId: clientId,
          employeeId: manager.employeeId || manager.id, // Use Employee.id
          locationId: locationId,
          roleDefinitionId: manager.roleDefinitionId || 'STORE_MANAGER', // Use provided role or default
          isPrimary: true,
          isActive: true,
          effectiveDate: effectiveDateISO,  // Use properly formatted ISO date
          assignedBy: manager.assignedBy,  // This is required, no null allowed
          assignmentReason: manager.assignmentReason || null,
          employeeNameSnapshot: managerFullName,
          locationNameSnapshot: locationName,
          roleSnapshot: manager.roleSnapshot || 'Store Manager', // Use provided role snapshot or default
          createdAt: now,  // Required field
          lastModified: now,  // Required field
          changedBy: manager.assignedBy  // Track who made the change
        };

        console.log('📝 Creating LocationEmployee with input:', JSON.stringify(input, null, 2));
        console.log('📅 Date conversion: ', {
          original: manager.effectiveDate,
          converted: effectiveDateISO
        });
        
        return apiClient.mutate(createLocationEmployee, { input });
      });

      const results = await Promise.all(createPromises);

      // Create audit log entry for each new manager assignment
      if (newManagers.length > 0) {
        // Create individual audit log for each new manager assignment
        const auditPromises = newManagers.map(async (manager) => {
          try {
            const managerName = `${manager.firstName} ${manager.lastName}`.trim() || manager.userName;
            
            // Build the list of all managers (existing + new)
            const allManagerNames = [...existingManagerNames, managerName];
            const oldValue = existingManagerNames.length > 0 ? existingManagerNames.join(', ') : null;
            const newValue = allManagerNames.join(', ');
            
            await this.createAuditLog({
              clientId: clientId,
              entityType: 'ManagerAssignment',  // Changed to reflect the actual table being modified
              entityId: locationId,
              entityName: `${locationName} - Manager Assignment`,
              changeType: 'MANAGER_ASSIGNMENT',
              changeSummary: `Added ${managerName} as Location Manager for ${locationName}`,
              changeReason: manager.assignmentReason || 'Manager assignment',
              changedBy: manager.assignedBy,
              changedByName: manager.assignedByName || 'System',
              affectedLocationId: locationId,  // Track which location is affected
              fieldChanges: [{
                fieldName: 'Location Managers',
                oldValue: oldValue,
                newValue: newValue,
                oldDisplayValue: oldValue || 'No managers',
                newDisplayValue: newValue
              }]
            });
          } catch (auditError) {
            console.error(`Failed to create audit log for manager ${manager.userName}:`, auditError);
            // Don't fail the whole operation if audit logging fails
          }
        });
        
        await Promise.all(auditPromises);
      }
      
      return results.map(r => r.data.createLocationEmployee);
    } catch (error) {
      console.error('❌ Error updating location managers:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      if (error.errors) {
        error.errors.forEach(e => {
          console.error('GraphQL Error:', e.message);
          if (e.errorType) console.error('Error Type:', e.errorType);
          if (e.data) console.error('Error Data:', e.data);
        });
      }
      throw error;
    }
  }

  // Remove specific managers from a location
  async removeLocationManagers(locationId, managerIdsToRemove, clientId, locationName = 'Location', reasonForChange = 'Manager removed') {
    try {
      // Fetch existing location employees
      const existingAssignments = await apiClient.query(locationEmployeesByLocation, {
        locationId: locationId,
        filter: {
          clientId: { eq: clientId },
          isActive: { eq: true }
        }
      });

      const allManagers = existingAssignments?.data?.locationEmployeesByLocation?.items || [];
      
      // Filter to only the managers we want to remove
      const managersToRemove = allManagers.filter(m => 
        managerIdsToRemove.includes(m.employeeId) || managerIdsToRemove.includes(m.id)
      );
      
      // Get remaining managers for audit log
      const remainingManagers = allManagers.filter(m => 
        !managerIdsToRemove.includes(m.employeeId) && !managerIdsToRemove.includes(m.id)
      );
      const remainingManagerNames = remainingManagers.map(m => m.employeeNameSnapshot || 'Unknown');
      
      // Deactivate the specified managers
      const now = new Date().toISOString();
      const deactivatePromises = managersToRemove.map(
        assignment => {
          const updateInput = {
            id: assignment.id,
            isActive: false,
            endDate: now,
            changeReason: reasonForChange || null,
            lastModified: now  // Update the lastModified timestamp
          };
          
          console.log('📝 Updating LocationEmployee with input:', JSON.stringify(updateInput, null, 2));
          
          return apiClient.mutate(updateLocationEmployee, {
            input: updateInput
          });
        }
      );

      await Promise.all(deactivatePromises);
      
      // Create audit logs for removals
      const auditPromises = managersToRemove.map(async (manager) => {
        try {
          const removedManagerName = manager.employeeNameSnapshot || 'Unknown Manager';
          const allManagerNamesBefore = allManagers.map(m => m.employeeNameSnapshot || 'Unknown').join(', ');
          const allManagerNamesAfter = remainingManagerNames.length > 0 ? remainingManagerNames.join(', ') : null;
          
          await this.createAuditLog({
            clientId: clientId,
            entityType: 'ManagerAssignment',
            entityId: locationId,
            entityName: `${locationName} - Manager Assignment`,
            changeType: 'MANAGER_ASSIGNMENT',
            changeSummary: `Removed ${removedManagerName} as Location Manager from ${locationName}`,
            changeReason: reasonForChange,
            changedBy: manager.changedBy || manager.assignedBy,
            changedByName: 'System',
            affectedLocationId: locationId,
            fieldChanges: [{
              fieldName: 'Location Managers',
              oldValue: allManagerNamesBefore,
              newValue: allManagerNamesAfter,
              oldDisplayValue: allManagerNamesBefore,
              newDisplayValue: allManagerNamesAfter || 'No managers'
            }]
          });
        } catch (auditError) {
          console.error(`Failed to create audit log for manager removal:`, auditError);
        }
      });
      
      await Promise.all(auditPromises);

      return managersToRemove;
    } catch (error) {
      console.error('❌ Error removing location managers:', error);
      throw error;
    }
  }

  // Use the historyService for audit logging
  async createAuditLog(logData) {
    const { default: historyService } = await import('./historyService');
    return historyService.createAuditLog(logData);
  }
}

export default new ManagerService();